import React, { useState } from 'react';
import { resolveRequestPaymentRail } from './requestPaymentMethod';
import { dubaiDate } from '../../../utils/dubaiTime';
import './requestFundingSummary.css';

const money = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    return `AED ${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// "···1111" from whatever masked-PAN format the gateway stored.
const maskedTail = (masked) => {
    const digits = String(masked || '').replace(/[^0-9]/g, '');
    return digits.length >= 4 ? `···${digits.slice(-4)}` : null;
};

/**
 * THE APPROVAL GATE, in one place (F5).
 *
 * `funding_error` is set by routes/clientChangeRequests.js when
 * computeRequestFunding THREW. It is not "the breakdown is missing" — it is an
 * explicit instruction not to approve, because the server could not prove where
 * the money would come from. Until this landed, nothing in portal/src read the
 * field at all: a reviewer could approve completely blind on the one screen
 * that exists to stop exactly that.
 *
 * `blockers` are the coded refusals computeRequestFunding raises (unpaid
 * checkout, pending/rejected funding deposit, …). The approve route hard-409s
 * on them, so disabling the button is a faithful mirror of the server rather
 * than a second opinion.
 *
 * enforceBlockers — NOT every approve path checks blockers. The create_booking
 * and edit_booking branches do; the cancel_booking branch deliberately does
 * NOT, because a cancellation REFUNDS money and must never be held hostage to
 * a funding problem with the original charge. Callers on that path pass false,
 * and blockers are shown as context instead of as a gate. Do not "simplify"
 * this to always-true: it would make refunds unapprovable.
 */
export function fundingGate(request, detail, { enforceBlockers = true } = {}) {
    const fundingError = detail?.funding_error || request?.funding_error || null;
    if (fundingError) return { blocked: true, reason: fundingError };

    if (enforceBlockers) {
        const funding = detail?.funding || request?.funding || null;
        const blockers = Array.isArray(funding?.blockers) ? funding.blockers : [];
        if (blockers.length > 0) {
            return { blocked: true, reason: blockers[0].message || blockers[0].code };
        }
    }
    return { blocked: false, reason: null };
}

/**
 * The client's wallet figure as an ADMIN sees it, on the same terms the client
 * does (F4). `available_balance` is UNCLAMPED under the current balance model,
 * so a bare "AED −412.00 avail." is a number a reviewer cannot act on: it does
 * not say whether the client is in trouble or simply has requests in flight.
 *
 * Every negative therefore carries the committed breakdown that explains it.
 * The rows come from the SERVER (`committed_breakdown`, whose amounts sum
 * exactly to total − available) — this component does no arithmetic of its own
 * beyond summing what it was handed, so it cannot invent a netted figure.
 *
 * `adjustments_due_total` is rendered as a separate labelled "owed" line and is
 * never folded into the headline: it is money the client OWES, not wallet money
 * set aside, and netting it made the wallet look like it had shrunk when it had
 * not moved.
 */
export function RequestWalletFigure({ wallet }) {
    // P4. The breakdown rows and the resolution paths live behind an expand.
    // Hooks must run before the early return below — React does not allow a
    // conditional hook, and `wallet` is legitimately null on a fresh modal.
    const [expanded, setExpanded] = useState(false);

    if (!wallet) return null;

    const total = Number(wallet.total_balance || 0);
    const available = Number(wallet.available_balance);
    const hasAvailable = Number.isFinite(available);
    const negative = hasAvailable && available < -0.005;

    const rows = (Array.isArray(wallet.committed_breakdown) ? wallet.committed_breakdown : [])
        .filter((r) => Number(r.amount || 0) > 0.005);
    const committed = Number(
        wallet.committed_total != null
            ? wallet.committed_total
            : rows.reduce((s, r) => s + Number(r.amount || 0), 0),
    );
    const owed = Number(wallet.adjustments_due_total || 0);

    return (
        <div className="rwf">
            {wallet.wallet_label && (
                <div className="rwf__line"><span>{wallet.wallet_label} wallet</span></div>
            )}
            <div className="rwf__line">
                <span>{money(total)} total</span>
                {hasAvailable && (
                    <span className={negative ? 'rwf__avail is-negative' : 'rwf__avail'}>
                        {money(available)} available
                    </span>
                )}
            </div>

            {/* P4 — THE DETAIL IS BEHIND AN EXPAND, NOT DELETED.
                The committed rows are the evidence for the shortfall sentence
                below, so they must stay reachable on the same screen; what they
                must NOT do is bury the one line a reviewer needs to read. They
                are rendered (in the DOM, collapsed) rather than conditionally
                mounted so the figures are always present for anything that
                inspects this block. */}
            {rows.length > 0 && (
                <div className="rwf__detail">
                    <button
                        type="button"
                        className="rwf__toggle"
                        aria-expanded={expanded}
                        onClick={() => setExpanded((v) => !v)}
                    >
                        {expanded ? 'Hide' : 'Show'} what is committed
                        {committed > 0.005 ? ` (${money(committed)})` : ''}
                    </button>
                    <div className={`rwf__rows ${expanded ? '' : 'is-collapsed'}`}>
                        {rows.map((r) => (
                            <div className="rwf__row" key={r.key || r.label}>
                                <span>{r.label}{Number(r.count) > 0 ? ` · ${r.count}` : ''}</span>
                                <span>−{money(r.amount)}</span>
                            </div>
                        ))}
                        {/* P5's four sanctioned resolutions. Moved in here with the
                            rest of the detail: an operator who needs them is already
                            expanding, and leaving them inline is what made the
                            shortfall line unreadable. NONE of them is "force the
                            charge through" — an available balance below zero is
                            never resolved by spending more of it. */}
                        {negative && (
                            <div className="rwf__resolve">
                                To resolve: ask the client to top up the shortfall, cancel a pending
                                withdrawal or reject a pending request (either releases its hold
                                immediately), or — as a last resort — raise a price adjustment so the
                                amount is billed outside the wallet.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {owed > 0.005 && (
                <div className="rwf__row rwf__row--owed">
                    <span>
                        Owed (price adjustments)
                        {Number(wallet.adjustments_due_count) > 0 ? ` · ${wallet.adjustments_due_count}` : ''}
                    </span>
                    <span>{money(owed)}</span>
                </div>
            )}

            {/* A NEGATIVE MUST NEVER APPEAR BARE.
                P4 — SHORTENED. This used to run to four lines of prose and staff
                stopped reading it, which defeats the whole point of refusing to
                print a naked negative. What survives is the only thing a reviewer
                must act on: how much is committed, how short they are, and that
                wallet spending will fail. The evidence (the committed rows) and
                the ways out now live behind the expand above. The phrase
                "AED X is already committed" is deliberately preserved — it is the
                sentence that makes the negative explicable. */}
            {negative && (
                <div className="rwf__explain">
                    {committed > 0.005
                        ? `${money(committed)} is already committed`
                        : 'Commitments already recorded exceed the balance'}, leaving them{' '}
                    {money(Math.abs(available))} short. Wallet spending will fail until they top
                    up or a commitment is released.
                </div>
            )}
        </div>
    );
}

const RAIL_LABELS = {
    card: 'card',
    wallet: 'wallet',
    wallet_card: 'wallet + card',
    bank_transfer: 'bank transfer',
    auto_charge: 'saved card (on completion)',
    invoice_later: 'invoice',
};

/**
 * WHICH RAIL ACTUALLY MOVED THE MONEY.
 *
 * Still exported for CancelRequestReview, which needs the card/wallet facts to
 * decide whether the "refundable" figure can be shown as a plain number. A
 * MONEY decision, so it is resolved the same conservative way everything else
 * here is: from the SERVER's funding rows first, from the rail resolver only as
 * corroboration, and never from a guess.
 *
 * `unknown` is a first-class outcome. When we cannot prove which rail was used
 * we do NOT reshuffle anything on a guess — an unproven rail is precisely when
 * a reviewer most needs the conservative treatment.
 */
export function resolveRailFacts(request, detail) {
    const funding = detail?.funding || request?.funding || null;
    const rail = resolveRequestPaymentRail(request);
    const paid = Array.isArray(funding?.already_paid) ? funding.already_paid : [];

    let cardPaidAmount = 0;
    let walletPaidAmount = 0;
    paid.forEach((p) => {
        const amt = Number(p.amount || 0);
        if (!Number.isFinite(amt) || amt <= 0.005) return;
        // wallet_card is BOTH: its card half is real card money and its wallet
        // half is real wallet money, so it counts toward each. Collapsing it to
        // one rail would hide half the payment from the reviewer.
        if (p.rail === 'card' || p.rail === 'wallet_card') cardPaidAmount += amt;
        if (p.rail === 'wallet' || p.rail === 'wallet_card') walletPaidAmount += amt;
    });

    const charge = funding?.will_charge_on_approval || {};
    const chargeAmount = Number(charge.amount || 0);
    // Everything the approve path can debit on approval comes out of the wallet
    // (directly, or out of the deposit credited into it). A card is never
    // charged by clicking Approve.
    const walletChargeOnApproval = Number.isFinite(chargeAmount) && chargeAmount > 0.005
        ? chargeAmount
        : 0;

    if (funding) {
        const cardPaid = cardPaidAmount > 0.005;
        const walletUsed = walletPaidAmount > 0.005 || walletChargeOnApproval > 0.005;
        return {
            known: cardPaid || walletUsed,
            cardPaid,
            walletUsed,
            cardPaidAmount,
            walletPaidAmount,
            walletChargeOnApproval,
            rail,
        };
    }

    // No server summary: fall back to the rail resolver, which itself only ever
    // claims a rail it has positive evidence for (it never defaults to wallet).
    const cardPaid = rail.key === 'card' || rail.key === 'wallet_card';
    const walletUsed = rail.key === 'wallet' || rail.key === 'wallet_card';
    return {
        known: cardPaid || walletUsed,
        cardPaid,
        walletUsed,
        cardPaidAmount: 0,
        walletPaidAmount: 0,
        walletChargeOnApproval: 0,
        rail,
    };
}

// Copy for legs that mean "no money backs this request" — the reason comes
// from the server stamp, never inferred here.
const NONE_LEG_COPY = {
    no_payment_required: 'No payment required for this request.',
    no_charge_required: 'Nothing to charge for this request.',
    refund_or_no_charge: 'Nothing to charge — any refund is credited back to the client.',
    approved_as_pending_unpaid: 'Approved as Pending — nothing has been charged.',
};

/**
 * funding.funding_legs (A1, server contract) → renderable rows.
 *
 * Legacy degradation: a payload from before funding_legs shipped still has
 * `already_paid` + `will_charge_on_approval`, so equivalent rows are derived
 * from those. Derived, not invented — every row still comes from a server
 * figure the approve path itself uses.
 *
 * The coverage guard at the end is the money contract pointing forward: if the
 * server says approval WILL charge and no rendered row says so, a synthesized
 * "charged on approval" row is added rather than hiding the debit. (Skipped
 * while a card capture is still in flight — the transient chargeAmount there
 * deliberately double-reads against the awaiting-capture leg.)
 */
function buildLegRows(funding) {
    const legs = Array.isArray(funding.funding_legs) ? funding.funding_legs : [];
    const paid = Array.isArray(funding.already_paid) ? funding.already_paid : [];
    const charge = funding.will_charge_on_approval || {};
    const chargeAmount = Number(charge.amount || 0);
    const rows = [];

    const pushCard = (amount, when, masked, awaiting, checkoutStatus) => rows.push({
        key: `card-${rows.length}`,
        label: 'Card',
        meta: awaiting
            ? `payment ${checkoutStatus && checkoutStatus !== 'created' ? checkoutStatus : 'not confirmed yet'}`
            : [when ? dubaiDate(when) : null, maskedTail(masked)].filter(Boolean).join(' · ') || null,
        amount,
        tone: awaiting ? 'warn' : 'paid',
    });

    if (legs.length > 0) {
        legs.forEach((leg) => {
            const amt = leg.amount != null ? Number(leg.amount) : null;
            switch (leg.kind) {
                case 'card':
                    pushCard(amt, leg.when, leg.card_masked, leg.timing === 'awaiting_capture', leg.checkout_status);
                    break;
                case 'bank_transfer':
                    rows.push({
                        key: `bank-${rows.length}`,
                        label: 'Bank transfer',
                        meta: leg.when ? dubaiDate(leg.when) : null,
                        amount: amt,
                        tone: 'paid',
                    });
                    break;
                case 'auto_charge':
                    rows.push({
                        key: 'auto',
                        label: 'Auto payment',
                        meta: [maskedTail(leg.card_masked), 'charged at completion'].filter(Boolean).join(' · '),
                        amount: amt > 0.005 ? amt : null,
                        tone: 'future',
                    });
                    break;
                case 'wallet':
                    rows.push({
                        key: 'wallet',
                        label: 'Wallet',
                        meta: leg.timing === 'on_approval'
                            ? `charged on approval${Number(leg.reserved_amount || 0) > 0.005 ? ' · already reserved' : ''}`
                            : null,
                        amount: amt,
                        tone: leg.timing === 'on_approval' ? 'charge' : 'paid',
                    });
                    break;
                case 'none':
                    rows.push({
                        key: 'none',
                        text: NONE_LEG_COPY[leg.reason] || 'No payment backs this request.',
                        tone: 'muted',
                    });
                    break;
                case 'recorded_before_rails':
                default:
                    rows.push({
                        key: 'prerails',
                        text: 'How this request was paid was not recorded (it predates payment records). '
                            + 'Check the client’s wallet ledger before approving.',
                        tone: 'warn',
                    });
                    break;
            }
        });
    } else {
        // Legacy payload without funding_legs — derive the same rows.
        paid.forEach((p) => {
            const amt = Number(p.amount || 0);
            if (p.rail === 'card' || p.rail === 'wallet_card') {
                pushCard(amt, p.captured_at, null, false, null);
            } else if (p.rail === 'bank_transfer') {
                rows.push({
                    key: `bank-${rows.length}`,
                    label: 'Bank transfer',
                    meta: p.captured_at ? dubaiDate(p.captured_at) : null,
                    amount: amt,
                    tone: 'paid',
                });
            } else if (p.rail === 'wallet') {
                rows.push({
                    key: `wallet-paid-${rows.length}`,
                    label: 'Wallet',
                    meta: p.captured_at ? `${dubaiDate(p.captured_at)} · already taken` : 'already taken',
                    amount: amt,
                    tone: 'paid',
                });
            } else if (p.rail === 'auto_charge') {
                rows.push({
                    key: 'auto',
                    label: 'Auto payment',
                    meta: 'charged at completion',
                    amount: null,
                    tone: 'future',
                });
            }
        });
        if (chargeAmount > 0.005) {
            rows.push({
                key: 'wallet',
                label: 'Wallet',
                meta: 'charged on approval',
                amount: chargeAmount,
                tone: 'charge',
            });
        }
    }

    // COVERAGE GUARD — a real charge must always have a row saying so.
    const hasOnApproval = rows.some((r) => r.tone === 'charge');
    const hasAwaiting = rows.some((r) => r.tone === 'warn' && r.label === 'Card');
    if (chargeAmount > 0.005 && !hasOnApproval && !hasAwaiting) {
        rows.push({
            key: 'charge-guard',
            label: 'Charged on approval',
            meta: null,
            amount: chargeAmount,
            tone: 'charge',
        });
    }

    return rows;
}

/**
 * ONE PAYMENT LIST (A5, 2026-07-19).
 *
 * The old review screens stated "paid by card" up to four separate ways —
 * banner, wallet section, funding rows, closing sentence — while never stating
 * the actual wallet+card split. This component is now the ONLY place on the
 * review screens that talks about payment, rendered once, AFTER the price
 * summary. It renders the server's `funding_legs` array (computed by the same
 * helper the approve path uses) as one compact row per leg:
 *
 *     Wallet · charged on approval        AED 245.70
 *     Card · 18/07/2026 · ···1111         AED 480.00
 *     Auto payment · charged at completion
 *
 * then the client's balance after approval when a wallet charge happens on
 * approval, then AT MOST ONE explanatory line — the server's own statement
 * verbatim when present (it is the only thing that knows which of the seven
 * buildStatement branches applies), a conservative fallback otherwise. Under
 * the money contract a sentence we cannot verify is worse than an explicit
 * "not shown", so a missing statement is never reconstructed.
 *
 * WITHOUT a server summary we do NOT invent one: the fallback states the cost,
 * states the rail we have evidence for, and — for any rail that already took
 * money — explicitly refuses to show a deduction, telling the reviewer to
 * verify instead. A missing summary degrades to "we don't know", never to a
 * confident wrong number.
 */
export default function RequestFundingSummary({
    request,
    detail,
    cost,
    deltaLabel = null,
    enforceBlockers = true,
    readOnly = false,
}) {
    const funding = detail?.funding || request?.funding || null;
    const rail = resolveRequestPaymentRail(request);
    const costTotal = funding?.cost_total != null ? Number(funding.cost_total) : Number(cost);

    // F5. Rendered in BOTH branches. The server only sets this alongside
    // funding === null today, but binding it to the fallback branch alone made
    // that coincidence load-bearing — if the server ever sends a partial
    // summary with an error the reviewer must still see the warning.
    const fundingError = detail?.funding_error || request?.funding_error || null;

    // ---- server-computed path: THE one payment list ---------------------
    if (funding) {
        const paid = Array.isArray(funding.already_paid) ? funding.already_paid : [];
        const paidTotal = paid.reduce((s, p) => s + Number(p.amount || 0), 0);
        const charge = funding.will_charge_on_approval || {};
        const chargeAmount = Number(charge.amount || 0);
        // Rendered verbatim — see the note beside rfs__note below.
        const statement = typeof charge.statement === 'string' && charge.statement.trim()
            ? charge.statement.trim()
            : null;
        const alloc = funding.capture_allocation || null;
        const after = funding.balance_after || null;
        const blockers = Array.isArray(funding.blockers) ? funding.blockers : [];

        const rows = buildLegRows(funding);
        const walletChargeOnApproval = rows
            .filter((r) => r.tone === 'charge')
            .reduce((s, r) => s + Number(r.amount || 0), 0);

        const fullyPrepaid = paidTotal > 0.005 && chargeAmount <= 0.005;
        // A direct-rail card payment never entered the wallet. Only say
        // "credited to wallet" where the server explicitly reports it — the
        // legacy round-trip rows do, the direct ones send false.
        const anyCreditedToWallet = paid.some((p) => p.credited_to_wallet === true);

        // AT MOST ONE explanatory line (owner ruling). Priority: the server's
        // own sentence (requestFundingService.buildStatement — the only thing
        // that knows which of its seven branches applies, computed from the
        // same figures the approve path uses; do NOT reintroduce a
        // locally-composed sentence), then conservative fallbacks for older
        // payloads that carry no statement.
        const line = statement
            || (fullyPrepaid && anyCreditedToWallet
                ? 'This payment was credited to the client’s wallet; approval consumes '
                  + 'exactly that credit and is not a second charge.'
                : fullyPrepaid
                    ? 'Already paid in full — approving charges nothing.'
                    : paidTotal > 0.005
                        // No statement in the payload (older/partial response). We
                        // do not reconstruct one: under the money contract a
                        // sentence we cannot verify is worse than an explicit
                        // "not shown".
                        ? `Money has already been paid toward this request (${money(paidTotal)} itemised above). `
                          + 'The server did not send its funding statement, so how much of the figure above is '
                          + 'that same money — rather than a fresh debit — is not shown here rather than guessed. '
                          + 'Check the client’s wallet ledger before approving.'
                        : chargeAmount > 0.005 && charge.source === 'linked_deposit'
                            ? `Taken from the deposit this request was paid into${charge.deposit_id ? ` (#${charge.deposit_id})` : ''}, not from the client’s other wallet funds.`
                            : chargeAmount > 0.005
                                ? 'Taken from the client’s wallet balance on approval.'
                                : 'Nothing is charged on approval.');

        return (
            <div className="rfs">
                <div className="rfs__title">Payment</div>

                {fundingError && (
                    <div className="rfs__note rfs__note--warn">{fundingError}</div>
                )}

                <div className="rfs__legs">
                    {rows.map((r) => (r.text ? (
                        <div className={`rfs__legText rfs__legText--${r.tone}`} key={r.key}>
                            {r.text}
                        </div>
                    ) : (
                        <div className="rfs__leg" key={r.key}>
                            <span className="rfs__legLabel">
                                {r.label}
                                {r.meta ? <span className={`rfs__legMeta ${r.tone === 'warn' ? 'is-warn' : ''}`}> · {r.meta}</span> : null}
                            </span>
                            {r.amount != null && Number.isFinite(Number(r.amount)) && (
                                <strong className={`rfs__legAmt rfs__legAmt--${r.tone}`}>{money(r.amount)}</strong>
                            )}
                        </div>
                    )))}

                    {/* A COMBINED capture must show only THIS booking's share as its
                        card row, or the reviewer reads the whole capture as payment
                        for the booking. One muted line accounts for the rest. */}
                    {alloc && alloc.is_combined && Number(alloc.capture_total || 0) > 0 && (
                        <div className="rfs__legSub">
                            Part of one {money(alloc.capture_total)} card payment
                            {Number(alloc.pending_invoices || 0) > 0 ? <> · pending invoices {money(alloc.pending_invoices)}</> : null}
                            {Number(alloc.adjustments || 0) > 0 ? <> · price adjustments {money(alloc.adjustments)}</> : null}
                            {Number(alloc.topup_surplus || 0) > 0 ? <> · {money(alloc.topup_surplus)} left in wallet</> : null}
                        </div>
                    )}
                </div>

                {/* Balance-after, only when approval actually debits the wallet.
                    ONE figure — available (item 12: adjustments never net into
                    any balance figure, and total beside available invited the
                    old netted-figure confusion back in). */}
                {walletChargeOnApproval > 0.005 && after && Number.isFinite(Number(after.available)) && (
                    <div className="rfs__row rfs__row--after">
                        <span>Client balance after approval</span>
                        <span>{money(after.available)} available</span>
                    </div>
                )}

                <div className="rfs__note">
                    {line}
                    {charge.consumes_bonus === true && (
                        <span className="rfs__warnInline"> This would consume promo bonus credit.</span>
                    )}
                </div>

                {/* The heading tracks whether this approve path ACTUALLY refuses
                    on blockers. The cancel branch does not check them (a refund
                    must not be held hostage to a funding problem with the
                    original charge), so claiming "Approval is blocked" there
                    would be a false statement about what the button will do. */}
                {/* Hidden in read-only history views (2026-08-06: an
                    approval gate on a decided request is noise) and DEDUPED —
                    the server can raise overlapping blockers for one cause. */}
                {!readOnly && blockers.length > 0 && (
                    <div className={`rfs__blockers ${enforceBlockers ? '' : 'rfs__blockers--advisory'}`}>
                        <div className="rfs__blockersTitle">
                            {enforceBlockers ? 'Approval is blocked' : 'Worth knowing before you approve'}
                        </div>
                        {[...new Map(blockers.map((b) => [b.message || b.code, b])).values()].map((b, i) => (
                            <div className="rfs__blocker" key={b.code || i}>{b.message || b.code}</div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // ---- no server summary: state what we know, claim nothing else ------
    // Rails that have ALREADY taken money. For these we must not render a
    // deduction — that is exactly the "paid by card, now charging again" screen
    // The owner called completely broken.
    const alreadyPaidRail = ['card', 'wallet_card', 'bank_transfer'].includes(rail.key);
    const nothingTakenYet = ['auto_charge', 'invoice_later'].includes(rail.key);

    // `fundingError` is resolved at the top of the component — it is a stronger
    // statement than "no breakdown was returned" (it is an explicit instruction
    // not to approve), so it is surfaced verbatim and outranks the generic copy
    // below.
    return (
        <div className="rfs">
            <div className="rfs__title">Payment</div>

            <div className="rfs__row">
                <span>{deltaLabel || 'This booking costs'}</span>
                <strong>{money(costTotal)}</strong>
            </div>

            <div className="rfs__row rfs__row--paid">
                <span>Payment rail on record</span>
                <strong>{rail.label}</strong>
            </div>

            {fundingError && (
                <div className="rfs__note rfs__note--warn">{fundingError}</div>
            )}

            {alreadyPaidRail ? (
                // A card payment for a booking is a payment FOR THE BOOKING, not a
                // wallet top-up (owner, repeatedly). This copy used to assert that
                // the capture "is credited to their wallet" — true only of the
                // legacy round trip, and exactly the confusion being removed. With
                // no funding breakdown we do not know which rail applies, so we
                // assert neither and tell the reviewer where to look.
                <div className="rfs__note rfs__note--warn">
                    The client has already paid for this by {RAIL_LABELS[rail.key] || rail.key}, so approving
                    should not charge them again. This server has not sent a funding breakdown, so the exact
                    figure — and whether any residual is taken from the wallet — is not shown here rather than
                    guessed. Check the booking’s payments before approving.
                </div>
            ) : nothingTakenYet ? (
                <div className="rfs__note">
                    Nothing has been taken yet on this rail — approving does not charge the client now.
                </div>
            ) : !fundingError ? (
                // Suppressed when `funding_error` is present: that message already
                // says the breakdown is missing, and in stronger terms.
                <div className="rfs__note rfs__note--warn">
                    No funding breakdown was returned for this request, so what approval will debit is not
                    shown here rather than guessed. Verify the client’s wallet balance before approving.
                </div>
            ) : null}
        </div>
    );
}
